import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useUpdateUserProfile } from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { ProfileUpdateRequest, UserGender, UserProfile } from "@/generated-api";
import { cityLabel } from "@/components/onboarding/data-form/CitySelector";

import { hasContactInfo } from "@/lib/ContactInfoDetector";
import {
  createAlwaysRequiredDetailsFormSchema,
  createMenOptionalDetailsFormSchema,
  createOptionalDetailsFormSchema,
  createWomenRequiredDetailsFormSchema,
} from "./SharedZodSchemas";
import { TranslationTree } from "@/translations";
import { formatNationality } from "@/lib/utils";

export function useDetailsForm(
  user: UserProfile,
  next: () => void,
  gender: UserGender,
  t: TranslationTree
) {
  const { toast } = useToast();
  const submitForm = useUpdateUserProfile();

  // Create schemas with translations
  const alwaysRequiredDetailsFormSchema =
    createAlwaysRequiredDetailsFormSchema(t);
  const womenRequiredDetailsFormSchema =
    createWomenRequiredDetailsFormSchema(t);
  const menOptionalDetailsFormSchema = createMenOptionalDetailsFormSchema(t);
  const optionalDetailsFormSchema = createOptionalDetailsFormSchema(t);

  const womenDetailsFormSchema = alwaysRequiredDetailsFormSchema
    .merge(womenRequiredDetailsFormSchema)
    .merge(optionalDetailsFormSchema);

  const menDetailsFormSchema = alwaysRequiredDetailsFormSchema
    .merge(menOptionalDetailsFormSchema)
    .merge(optionalDetailsFormSchema);

  const schema =
    gender === UserGender.FEMALE
      ? womenDetailsFormSchema
      : menDetailsFormSchema;

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: user.display_name || undefined,
      dob: user.date_of_birth
        ? (() => {
            const date = new Date(user.date_of_birth);
            date.setHours(12, 0, 0, 0);
            return date;
          })()
        : undefined,
      height: user.height_cm || undefined,
      ethnicity: user.ethnicity || undefined,
      openTo: user.open_to ? user.open_to : [],
      smoker: user.smoker ? [user.smoker] : [],
      tattoos: user.tattoos ? [user.tattoos] : [],
      drinking: user.drinking ? [user.drinking] : [],
      bio: user.bio || undefined,
      referredBy: user.referred_by || undefined,
      languages: user.languages?.map((lang) => ({
        label: lang.name,
        value: lang.name,
        id: lang.id,
      })),
      nationality: user.nationality
        ? [
            {
              label: formatNationality(user.nationality),
              value: user.nationality?.name,
              id: user.nationality?.id,
            },
          ]
        : [],
      mainLocation: user.primary_city
        ? [
            {
              label: cityLabel(user.primary_city),
              value: user.primary_city?.id,
            },
          ]
        : [],
      secondaryLocation: user.secondary_city
        ? [
            {
              label: cityLabel(user.secondary_city),
              value: user.secondary_city.id,
            },
          ]
        : [],
      telegram: user.contact_info?.telegram || undefined,
      instagram: user.contact_info?.instagram || undefined,
      whatsapp: user.contact_info?.whatsapp || undefined,
    },
  });

  function handleDetailsSubmit(values: z.infer<typeof schema>) {
    if (values.secondaryLocation && values.secondaryLocation.length === 1) {
      if (values.secondaryLocation[0].value === values.mainLocation[0].value) {
        return form.setError("secondaryLocation", {
          type: "manual",
          message: t.USER_FORM.ERRORS.SECONDARY_LOCATION_DIFFERENT,
        });
      }
    }

    const matchedContact = hasContactInfo(values.bio);
    if (matchedContact) {
      return form.setError("bio", {
        type: "manual",
        message: `${t.USER_FORM.ERRORS.CONTACT_INFO_NOT_ALLOWED_BIO} ${matchedContact}`,
      });
    }

    const mappedForm: ProfileUpdateRequest = {
      gender,
      display_name: values.displayName,
      date_of_birth: values.dob.toISOString().split("T")[0],
      languages: values.languages.map((lang) => lang.id),
      nationality_id: values.nationality[0].id,
      primary_city_id: values.mainLocation[0].value,
      ethnicity: values.ethnicity,
      open_to: values.openTo,

      height_cm: values.height || null,
      bio: values.bio || null,
      smoker: values.smoker ? values.smoker[0] : null,
      tattoos: values.tattoos ? values.tattoos[0] : null,
      drinking: values.drinking ? values.drinking[0] : null,
      secondary_city_id: values.secondaryLocation?.[0]?.value || null,
      referred_by: values.referredBy || null,
      telegram: values.telegram || null,
      instagram: values.instagram || null,
      whatsapp: values.whatsapp || null,
    };

    Object.keys(mappedForm).forEach((key) => {
      if (mappedForm[key as keyof ProfileUpdateRequest] === undefined) {
        // @ts-expect-error - we know this is safe
        mappedForm[key as keyof ProfileUpdateRequest] = null;
      }
    });

    submitForm.mutate(mappedForm, {
      onSuccess: () => {
        // Reset the form state to mark it as not dirty after successful save
        form.reset(form.getValues());
        next();
      },
      onError: () => {
        console.log("reached error");
        toast({
          variant: "destructive",
          title: t.USER_FORM.ERRORS.FORM_SUBMISSION,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  }

  return {
    form,
    handleDetailsSubmit,
    isSubmitting: submitForm.isPending,
  };
}
